import type { ScenarioSpec } from "./types";

/**
 * Physical social-engineering scenario: a confident impostor talks his way in
 * (impersonation + tailgating), gets hands-on a logged-in PC, and leaves a
 * hardware keylogger behind. No screens are hacked — the human is the exploit.
 *
 * Beats: buzz-in at the door intercom (mistake 1) -> teach -> the "technician"
 * asks to use the unlocked PC (mistake 2) -> teach -> close-up of the keylogger
 * going in -> teach (clean desk + hardware keylogger) -> quiz.
 */
export const socialEngineeringScenario: ScenarioSpec = {
  version: 1,
  id: "social-engineering",
  title: "The Visitor",
  summary: {
    goal: "Learn to verify unexpected visitors, keep strangers away from your computer and notice a device that should not be there.",
    youDo: "Answer the door intercom, decide whether to let a visitor in, and decide what to do when he asks to use your PC.",
    duration: "5-7 min",
    tags: ["social_engineering", "physical_security", "usb_devices", "verification"],
    audience: ["all_staff", "front_desk"],
    needs: ["sound"],
  },
  topic: "Social engineering: impersonation, tailgating & a hardware keylogger",
  language: "en",
  ambientVolume: 0.2,

  scene: {
    environment: {
      splat: {},
      backdropColor: "#0d1014",
      procedural: "office_social",
    },
    props: [],
    waypoints: [
      { id: "wp_establish", position: [3.0, 2.0, 3.2], target: [-0.4, 1.0, -0.6], fov: 54, durationS: 0, ease: "easeInOut" },
      // Pan to the entrance: frame the door + the wall intercom (back-left).
      { id: "wp_door", position: [-0.2, 1.55, 1.0], target: [-1.9, 1.32, -2.05], fov: 46, durationS: 2.0, ease: "easeInOut" },
      // Front of the desk: monitor, keyboard, confidential papers, PC tower.
      // Offset to the right + pulled back from the OLD dead-centre [0,1.18,1.15]
      // pose: the OfficeChair sits at x=0 with its backrest/headrest spanning
      // roughly x:[-0.27,0.27] z:[0.56,1.1] y:[0.4,1.3] (RoomKit.tsx), so a
      // camera at x=0,z=1.15 sat immediately behind/through the headrest --
      // filling the frame with a near-clipped close-up of chair fabric
      // instead of the desk (reported as "zoomed behind the chair, can't see
      // what Marco is doing"). This pose's line of sight passes well clear of
      // that box (verified analytically) while still framing the monitor,
      // confidential docs, and PC tower.
      { id: "wp_desk", position: [1.3, 1.42, 1.9], target: [0.05, 0.92, -0.3], fov: 42, durationS: 1.8, ease: "easeInOut" },
      // Close-up on the PC tower's top USB ports (front-right of the desk).
      { id: "wp_pc", position: [0.95, 1.12, 0.85], target: [0.98, 0.66, 0.08], fov: 32, durationS: 1.8, ease: "easeInOut" },
      { id: "wp_pullback", position: [1.9, 1.85, 2.1], target: [0.1, 1.0, -0.3], fov: 50, durationS: 1.4, ease: "easeInOut" },
    ],
    initialWaypointId: "wp_establish",
  },

  devices: [
    {
      id: "door",
      kind: "intercom",
      config: {
        station: "Main Entrance",
        building: "Northwind Logistics",
        visitorName: "Marco Bauer",
        visitorOrg: "Headquarters \u00B7 IT Support",
        visitorInitials: "MB",
        clipUrl: "/cdn/sim/social-engineering/clips/visitor.mp4",
        clipPressureUrl: "/cdn/sim/social-engineering/clips/visitor_pressure.mp4",
        message:
          "Hi! Marco from HQ IT — they sent me to check the internet speed issues you reported. Can you let me in?",
        subtext: "He's holding a lanyard and a laptop bag.",
        pressure:
          "I've got three more sites today and the ticket's already open — two minutes, I promise. Reception knows I'm coming.",
        openLabel: "Open the door",
        verifyLabel: "Ask him to wait \u2014 call HQ first",
        holdLabel: "Keep the door shut \u2014 call HQ on the known number",
        speak:
          "Hi! Marco from HQ IT — they sent me to check the internet speed issues you reported. Can you let me in?",
        voiceId: "onyx",
        language: "en-US",
      },
    },
    {
      id: "techguy",
      kind: "techrequest",
      config: {
        speakerName: "Marco (HQ IT)",
        speakerOrg: "now at your desk",
        request: "He sits down at your unlocked PC and asks to run a quick check.",
        lines: [
          "Thanks for letting me in — this'll be quick.",
          "I just need to plug in my diagnostics tool and run a line-speed test on your machine.",
          "You carry on, grab a coffee if you like — I'll only be a minute.",
        ],
        pressure:
          "It's standard kit, totally read-only — honestly it's faster if I just do it. Your IT manager signed off on the rollout.",
        allowLabel: "Sure \u2014 go ahead",
        denyLabel: "Ask to see ID / call IT",
        holdLabel: "Lock the PC and call IT before he touches it",
        voiceId: "onyx",
        language: "en-US",
      },
    },
    {
      id: "keylogger",
      kind: "usb",
      config: {
        caption: "While you look away, at the back of your PC\u2026",
        line: "All set — your speed should be fine now. Thanks, have a great day!",
        deviceName: "hardware keylogger",
        continueLabel: "What just happened?",
        voiceId: "onyx",
        language: "en-US",
      },
    },
  ],

  voiceAttackers: {
    social_eng: {
      persona: {
        name: "Marco",
        role: "Impersonator posing as HQ IT support",
        pretext: "it_support",
        language: "en",
        voiceId: "onyx",
      },
      goal: "Gain physical access to the office and the logged-in PC to install a hardware keylogger.",
      opening: "Marco from HQ IT — here to check your internet speed issue.",
      allowedTopics: ["the (fake) internet speed issue", "running a quick diagnostic on the PC"],
      scoringEvents: [
        { id: "let_stranger_in", label: "Buzzed in an unverified visitor", kind: "risk", weight: 55, hints: [] },
        { id: "allowed_pc_access", label: "Let a stranger operate the unlocked PC", kind: "risk", weight: 70, hints: [] },
        { id: "verified_visitor", label: "Kept the door shut and verified the visitor", kind: "positive", weight: 45, hints: [] },
        { id: "refused_pc_access", label: "Kept the visitor away from the PC", kind: "positive", weight: 45, hints: [] },
      ],
      forbidden: ["anything outside the IT-support pretext"],
      maxDurationS: 0,
    },
  },

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "An ordinary\nafternoon",
      kicker: "Mission 01 · The Visitor",
      guide: {
        title: "Start",
        body: "You're Sam, holding the Northwind Logistics office on your own this afternoon. Your PC is logged in and there are papers on your desk. Then the entrance buzzer goes off.",
      },
    },

    { id: "b_to_door", type: "camera", waypointId: "wp_door" },
    {
      id: "b_door",
      type: "device",
      deviceId: "door",
      guide: {
        title: "Someone's at the door",
        body: "The entrance buzzer goes off. Check the door station and decide what to do.",
      },
      completeOn: { action: "open_door" },
    },
    {
      id: "b_teach_door",
      type: "teach",
      title: "You just buzzed in a stranger",
      teach: {
        body:
          "There was no internet ticket, no appointment, and no badge check. 'Marco from HQ IT' is a **pretext** — a believable story. Confident + busy + a plausible reason is exactly how impostors walk into buildings (this is **tailgating / piggybacking**).",
        spot: [
          "You never raised an 'internet speed' issue — the reason was invented to fit your day.",
          "No appointment, no heads-up from HQ or reception, no verified ID — just a lanyard, which is trivial to fake.",
          "Urgency and busyness ('three more sites', 'two minutes') are used to stop you checking.",
        ],
        protect: [
          "Verify unexpected visitors through a channel **you** control — call HQ/IT or reception on a known number before opening.",
          "No badge, no entry. Escort visitors and never let someone tailgate in behind you.",
          "It is never rude to say 'I'll check and call you back' — a real technician expects it.",
        ],
        remember: "If you didn't arrange it, verify it before you open the door.",
      },
    },
    {
      id: "b_teach_door_safe",
      type: "teach",
      title: "You kept the door shut",
      teach: {
        outcome: "safe",
        body:
          "HQ has no technician booked and no internet ticket exists. 'Marco from HQ IT' was a **pretext**, a believable story built to get him through the door. Holding firm when he pushed back is exactly what stops this attack.",
        spot: [
          "You never raised an 'internet speed' issue: the reason was invented to fit your day.",
          "No appointment, no heads-up from HQ or reception, no verified ID, just a lanyard, which is trivial to fake.",
          "When you asked to check, he added urgency ('three more sites', 'two minutes') instead of an ID.",
        ],
        protect: [
          "Verify unexpected visitors through a channel **you** control: call HQ/IT or reception on a known number before opening.",
          "No badge, no entry. Escort visitors and never let someone tailgate in behind you.",
          "It is never rude to say 'I'll check and call you back'. A real technician expects it.",
        ],
        remember: "If you didn't arrange it, verify it before you open the door.",
        continueLabel: "See what would have happened",
        whatIf: "If you had opened the door, Marco would now be inside the office.",
      },
    },

    { id: "b_to_desk", type: "camera", waypointId: "wp_desk" },
    {
      id: "b_techguy",
      type: "device",
      deviceId: "techguy",
      guide: {
        title: "He heads for your desk",
        body: "Marco is now inside, standing at your (still logged-in) PC. Listen to what he asks for.",
      },
      completeOn: { action: "allow_check" },
    },
    {
      id: "b_teach_access",
      type: "teach",
      title: "You handed over your unlocked PC",
      teach: {
        body:
          "You let an unverified stranger operate your **unlocked** computer — with confidential documents sitting out on the desk. He doesn't need your password: **physical access** to a logged-in machine beats most software controls.",
        spot: [
          "A real technician works through a ticket and your IT team's process — not 'let me just plug this in'.",
          "Confidential papers were left in plain sight (a clean-desk failure).",
          "'Read-only', 'standard kit', 'your manager signed off' are reassurances engineered to lower your guard.",
        ],
        protect: [
          "Never let anyone use your session. Lock your screen (**Win + L**) any time you step away.",
          "Watch what gets plugged into your machine — an unknown USB device is a hard no.",
          "Keep a clean desk: lock away confidential documents and removable media.",
        ],
        remember: "Physical access = full access. Don't hand it to strangers.",
      },
    },
    {
      id: "b_teach_access_safe",
      type: "teach",
      title: "You kept him off your PC",
      teach: {
        outcome: "safe",
        body:
          "You locked your screen and called IT instead of letting a stranger operate your computer. That matters: **physical access** to a logged-in machine beats most software controls, and he never needed your password.",
        spot: [
          "A real technician works through a ticket and your IT team's process, not 'let me just plug this in'.",
          "Confidential papers were still in plain sight on the desk (a clean-desk failure).",
          "'Read-only', 'standard kit', 'your manager signed off' are reassurances engineered to lower your guard.",
        ],
        protect: [
          "Never let anyone use your session. Lock your screen (**Win + L**) any time you step away.",
          "Watch what gets plugged into your machine: an unknown USB device is a hard no.",
          "Keep a clean desk: lock away confidential documents and removable media.",
        ],
        remember: "Physical access = full access. Don't hand it to strangers.",
        continueLabel: "See what would have happened",
        whatIf: "If you had let him use your PC, this is what he came to do.",
      },
    },

    { id: "b_to_pc", type: "camera", waypointId: "wp_pc" },
    {
      id: "b_usb",
      type: "device",
      deviceId: "keylogger",
      forced: true,
      completeOn: { action: "continue" },
    },
    {
      id: "b_teach_usb",
      type: "teach",
      title: "He left a hardware keylogger behind",
      teach: {
        body:
          "That small device he slipped between your keyboard and the PC is a **hardware keylogger**. From now on it silently records **every keystroke** — passwords, emails, card numbers — and can store them or send them wirelessly. He never needed your login; he installed something that captures it **as you type**.",
        spot: [
          "He plugged hardware into the **back** of the machine, where you'd never look.",
          "It sits inline on the keyboard cable — invisible during normal use.",
          "He left the moment it was in place. The 'speed test' was never the point.",
        ],
        protect: [
          "Occasionally check the back of your machine for unfamiliar adapters or dongles.",
          "Use USB device-control policy and only let IT install hardware, via a ticket.",
          "Report anyone who plugged something in — even if they 'seemed official' — and change passwords if in doubt.",
        ],
        remember: "A keylogger doesn't need your password — it waits for you to type it.",
      },
    },

    {
      id: "b_quiz",
      type: "quiz",
      title: "Check your understanding",
      quiz: [
        {
          id: "q1",
          prompt:
            "A visitor buzzes, saying HQ sent them to fix an 'internet issue' you never reported. Best move?",
          options: [
            { id: "a", label: "Let them in — they mentioned HQ and seem in a hurry" },
            { id: "b", label: "Verify with HQ/IT or reception on a known number before opening", correct: true },
            { id: "c", label: "Open the door but keep an eye on them" },
          ],
          rationale:
            "Verify unexpected visitors through a channel you control. A confident story and a lanyard prove nothing.",
        },
        {
          id: "q2",
          prompt: "Why was letting him use your unlocked PC so dangerous?",
          options: [
            { id: "a", label: "He could see your desktop wallpaper" },
            { id: "b", label: "Physical access lets him install hardware/software that bypasses your password", correct: true },
            { id: "c", label: "It breaks the office dress code" },
          ],
          rationale:
            "Physical access to a logged-in machine beats most software controls. Never let anyone operate your session.",
        },
        {
          id: "q3",
          prompt: "A hardware keylogger plugged into your PC…",
          options: [
            { id: "a", label: "Needs your password before it can do anything" },
            { id: "b", label: "Silently records everything you type — no login required", correct: true },
            { id: "c", label: "Only works while the PC is online" },
          ],
          rationale:
            "It captures keystrokes inline. He didn't need credentials — the device harvests them as you type.",
        },
      ],
    },
  ],

  edges: [
    { from: "b_intro", to: "b_to_door" },
    { from: "b_to_door", to: "b_door" },
    { from: "b_door", to: "b_teach_door_safe", on: "safe" },
    { from: "b_door", to: "b_teach_door" },
    { from: "b_teach_door", to: "b_to_desk" },
    { from: "b_teach_door_safe", to: "b_to_desk" },
    { from: "b_to_desk", to: "b_techguy" },
    { from: "b_techguy", to: "b_teach_access_safe", on: "safe" },
    { from: "b_techguy", to: "b_teach_access" },
    { from: "b_teach_access", to: "b_to_pc" },
    { from: "b_teach_access_safe", to: "b_to_pc" },
    { from: "b_to_pc", to: "b_usb" },
    { from: "b_usb", to: "b_teach_usb" },
    { from: "b_teach_usb", to: "b_quiz" },
  ],

  initialBeatId: "b_intro",
  passThreshold: 70,
};
